/**
 * Drawer Component
 *
 * Slide-out drawer primitives for cart, menus, and side panels.
 * Built on Radix Dialog with liquid glass design system.
 *
 * @example
 * ```tsx
 * import { Drawer } from '@/ui';
 *
 * <Drawer.Overlay />
 * <Drawer.Content>
 *   <Drawer.Header>Header content</Drawer.Header>
 *   <Drawer.ScrollArea>Main content</Drawer.ScrollArea>
 *   <Drawer.Footer>Footer content</Drawer.Footer>
 * </Drawer.Content>
 * ```
 */

import * as React from 'react';
import { cn } from '@/shared/utils/utils';

// ============================================================================
// OVERLAY
// ============================================================================

type DrawerOverlayProps = React.HTMLAttributes<HTMLDivElement>;

const DrawerOverlay = React.forwardRef<HTMLDivElement, DrawerOverlayProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'fixed inset-0 bg-overlay/40 backdrop-blur-glass z-overlay',
          className
        )}
        {...props}
      />
    );
  }
);

DrawerOverlay.displayName = 'Drawer.Overlay';

// ============================================================================
// CONTENT
// ============================================================================

type DrawerContentProps = React.HTMLAttributes<HTMLDivElement>;

const DrawerContent = React.forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'fixed right-0 top-0 h-full w-drawer-mobile max-w-drawer md:w-drawer',
          'liquid-glass-thick shadow-liquid-glass-lg z-modal',
          'flex flex-col',
          'border-l border-[hsl(var(--liquid-glass-border-medium))]',
          'pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]',
          className
        )}
        {...props}
      />
    );
  }
);

DrawerContent.displayName = 'Drawer.Content';

// ============================================================================
// HEADER
// ============================================================================

type DrawerHeaderProps = React.HTMLAttributes<HTMLDivElement>;

const DrawerHeader = React.forwardRef<HTMLDivElement, DrawerHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'px-6 py-5 border-b border-[hsl(var(--neutral-color)/0.08)]',
          className
        )}
        {...props}
      />
    );
  }
);

DrawerHeader.displayName = 'Drawer.Header';

// ============================================================================
// SCROLL AREA
// ============================================================================

type DrawerScrollAreaProps = React.HTMLAttributes<HTMLDivElement>;

const DrawerScrollArea = React.forwardRef<HTMLDivElement, DrawerScrollAreaProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex-1 overflow-y-auto', className)}
        {...props}
      />
    );
  }
);

DrawerScrollArea.displayName = 'Drawer.ScrollArea';

// ============================================================================
// FOOTER
// ============================================================================

type DrawerFooterProps = React.HTMLAttributes<HTMLDivElement>;

const DrawerFooter = React.forwardRef<HTMLDivElement, DrawerFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'border-t border-[hsl(var(--liquid-glass-border-light))] liquid-glass-thin',
          className
        )}
        {...props}
      />
    );
  }
);

DrawerFooter.displayName = 'Drawer.Footer';

// ============================================================================
// EXPORTS
// ============================================================================

export const Drawer = {
  Overlay: DrawerOverlay,
  Content: DrawerContent,
  Header: DrawerHeader,
  ScrollArea: DrawerScrollArea,
  Footer: DrawerFooter,
};
